import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UsersService, ExternalRoleName } from '../shared/users.service';
import {
  CreateExternalUserRequest,
  CreateGrowerAccountRequest,
  GrowerAccountSummary,
  PlantSummary,
  RoleUserMappingSummary,
  ShuntingYardSummary,
  TransloadingStationSummary,
  UpdateExternalUserRoleMappingRequest
} from '../interfaces/api-responses';
import { NavbarComponent } from '../navbar/navbar.component';
import { HeaderComponent } from '../header/header.component';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';
import { ReferenceDataService } from '../shared/reference-data.service';
import { DropdownOption, SharedDropdownComponent } from '../shared/shared-dropdown.component';

const ROLE_NAME_MAP = {
  Admin: 'admin',
  Grower: 'grower',
  Transloader: 'transloader',
  Shunting: 'shunting',
  Scheduler: 'scheduler',
  Gatehouse: 'gatehouse',
  Receiver: 'receiver',
  Audit: 'audit'
} as const satisfies Record<ExternalRoleName, string>;

type OptionalNumberInput = string | number | null;

type RoleSection = {
  role: ExternalRoleName;
  label: string;
  mappingLabel?: string;
};

type ConfirmAction = 'activate' | 'deactivate';

type RoleUsersRecord = Record<ExternalRoleName, RoleUserMappingSummary[]>;
type RoleFilterRecord = Record<ExternalRoleName, string | null>;
type RoleUserFilterRecord = Record<ExternalRoleName, string | null>;
type RoleOpenRecord = Record<ExternalRoleName, boolean>;

/** One row per user for Scheduler (merged plants). */
interface SchedulerGroupedRow extends RoleUserMappingSummary {
  schedulerPlantIds: number[];
}

const ROLE_SECTIONS: RoleSection[] = [
  { role: 'Admin', label: 'Admins' },
  { role: 'Grower', label: 'Growers', mappingLabel: 'Grower Account' },
  { role: 'Transloader', label: 'Transloaders', mappingLabel: 'Transloading Station' },
  { role: 'Shunting', label: 'Shunting', mappingLabel: 'Shunting Yard' },
  { role: 'Scheduler', label: 'Schedulers', mappingLabel: 'Plant' },
  { role: 'Gatehouse', label: 'Gatehouse', mappingLabel: 'Plant' },
  { role: 'Receiver', label: 'Receivers', mappingLabel: 'Plant' },
  { role: 'Audit', label: 'Audit' }
];

const ROLE_OPTIONS: DropdownOption[] = ROLE_SECTIONS.map(section => ({
  value: section.role,
  label: section.role
}));

const ROLE_ID_MAP: Record<ExternalRoleName, number> = {
  Admin: 1,
  Grower: 2,
  Transloader: 3,
  Receiver: 4,
  Shunting: 5,
  Scheduler: 6,
  Gatehouse: 7,
  Audit: 8
};

const ROLE_WITH_MAPPING = new Set<ExternalRoleName>([
  'Grower',
  'Transloader',
  'Shunting',
  'Scheduler',
  'Gatehouse',
  'Receiver'
]);

const ROLE_WITH_USERNAME_FILTER = new Set<ExternalRoleName>([
  'Admin',
  'Grower',
  'Transloader',
  'Shunting',
  'Scheduler',
  'Gatehouse',
  'Receiver',
  'Audit'
]);

const EXCLUDED_PLANTS = new Set(['grantham', 'hull']);

/** Shunting role: only this plant may be selected when creating a Shunting user (per product rules). */
const SHUNTING_CREATE_PLANT_NAME = 'whittlesey';

const EMPTY_ROLE_USERS: RoleUsersRecord = {
  Admin: [],
  Grower: [],
  Transloader: [],
  Shunting: [],
  Scheduler: [],
  Gatehouse: [],
  Receiver: [],
  Audit: []
};

const EMPTY_ROLE_FILTERS: RoleFilterRecord = {
  Admin: null,
  Grower: null,
  Transloader: null,
  Shunting: null,
  Scheduler: null,
  Gatehouse: null,
  Receiver: null,
  Audit: null
};

const EMPTY_ROLE_USER_FILTERS: RoleUserFilterRecord = {
  Admin: null,
  Grower: null,
  Transloader: null,
  Shunting: null,
  Scheduler: null,
  Gatehouse: null,
  Receiver: null,
  Audit: null
};

const DEFAULT_ROLE_OPEN: RoleOpenRecord = {
  Admin: false,
  Grower: false,
  Transloader: false,
  Shunting: false,
  Scheduler: false,
  Gatehouse: false,
  Receiver: false,
  Audit: false
};


@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NavbarComponent,
    LoadingSpinnerComponent,
    HeaderComponent,
    SharedDropdownComponent
  ],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.css'
})
export class AdminComponent implements OnInit {

  /**
   * Get the persona name for this component
   * Always returns 'admin' to ensure correct behavior regardless of localStorage
   */
  private get personaName(): string {
    return 'admin';
  }

  // Lists
  readonly roleSections = ROLE_SECTIONS;
  roleUsers = signal<RoleUsersRecord>({ ...EMPTY_ROLE_USERS });
  roleFilters = signal<RoleFilterRecord>({ ...EMPTY_ROLE_FILTERS });
  roleUserFilters = signal<RoleUserFilterRecord>({ ...EMPTY_ROLE_USER_FILTERS });
  roleOpen = signal<RoleOpenRecord>({ ...DEFAULT_ROLE_OPEN });
  isLoading = signal(false);
  isReferenceLoading = signal(false);
  isCreateSaving = signal(false);
  errorText = signal<string | null>(null);
  growerAccounts = signal<GrowerAccountSummary[]>([]);
  transloadingStations = signal<TransloadingStationSummary[]>([]);
  plants = signal<PlantSummary[]>([]);
  shuntingYards = signal<ShuntingYardSummary[]>([]);

  // Create form
  create = {
    username: signal(''),
    employeeName: signal(''),
    role: signal<ExternalRoleName>('Shunting'),
    growerAccountId: signal<number | null>(null),
    transloadingStationId: signal<number | null>(null),
    shuntingPlantId: signal<number | null>(null),
    shuntingYardId: signal<number | null>(null),
    plantId: signal<number | null>(null),
    schedulerPlantIds: signal<number[]>([])
  };

  // Dropdown states
  currentUserId = signal<number | null>(null);
  showGrowerAccountModal = signal(false);
  growerAccountName = signal('');
  growerAccountVendorNumber = signal('');
  isGrowerAccountSaving = signal(false);
  growerAccountModalContext = signal<'create' | 'edit'>('create');

  // Edit grower account modal
  showEditGrowerAccountModal = signal(false);
  isEditGrowerAccountSaving = signal(false);
  editGrowerAccountModalName = signal('');
  editGrowerAccountModalVendorNumber = signal('');

  // Confirm modal
  showConfirmModal = signal(false);
  confirmTitle = signal('');
  confirmMessage = signal('');
  confirmAction = signal<ConfirmAction | null>(null);
  confirmUser = signal<RoleUserMappingSummary | null>(null);
  confirmRole = signal<ExternalRoleName | null>(null);

  // Edit user modal
  showEditModal = signal(false);
  isEditSaving = signal(false);
  editRole = signal<ExternalRoleName | null>(null);
  editUserId = signal<number | null>(null);
  editEmployeeName = signal('');
  editUsername = signal('');
  editGrowerAccountId = signal<number | null>(null);
  editTransloadingStationId = signal<number | null>(null);
  editShuntingPlantId = signal<number | null>(null);
  editShuntingYardId = signal<number | null>(null);
  editPlantId = signal<number | null>(null);
  editSchedulerPlantIds = signal<number[]>([]);
  editShuntingYards = signal<ShuntingYardSummary[]>([]);

  constructor(
    private readonly users: UsersService,
    private readonly referenceData: ReferenceDataService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.currentUserId.set(this.getCurrentUserId());
    void this.refreshLists();
    void this.loadReferenceData();
  }

  clearError(): void { this.errorText.set(null); }

  private getCurrentUserId(): number | null {
    const raw = localStorage.getItem('userID');
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  isCreateDisabled(): boolean {
    if (this.isLoading() || this.isReferenceLoading() || this.isCreateSaving()) return true;
    const username = this.create.username().trim();
    const employeeName = this.create.employeeName().trim();
    if (!username || !employeeName) return true;
    const role = this.create.role();
    if (role === 'Grower') return this.create.growerAccountId() === null;
    if (role === 'Transloader') return this.create.transloadingStationId() === null;
    if (role === 'Shunting') {
      return this.create.shuntingPlantId() === null || this.create.shuntingYardId() === null;
    }
    if (role === 'Scheduler') return this.create.schedulerPlantIds().length === 0;
    if (role === 'Gatehouse' || role === 'Receiver') return this.create.plantId() === null;
    return false;
  }

  isDeactivateDisabled(user: RoleUserMappingSummary): boolean {
    return this.currentUserId() === user.userID || !this.isUserActive(user);
  }

  isEditDisabled(user: RoleUserMappingSummary): boolean {
    return this.currentUserId() === user.userID;
  }

  isActivateDisabled(user: RoleUserMappingSummary, role: ExternalRoleName): boolean {
    if (this.isUserActive(user)) return true;
    if (!ROLE_WITH_MAPPING.has(role)) return false;
    return user.mappingID === null || user.mappingID === undefined;
  }

  private roleToName(role: ExternalRoleName): string {
    return ROLE_NAME_MAP[role];
  }

  private isExternalRoleName(value: string): value is ExternalRoleName {
    return Object.hasOwn(ROLE_NAME_MAP, value);
  }

  private toExternalRoleName(value: string): ExternalRoleName | null {
    if (this.isExternalRoleName(value)) {
      return value;
    }
    return null;
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error) {
      const message = error.message.trim();
      if (message) return message;
    }
    if (typeof error === 'object' && error !== null) {
      const maybeError = error as { error?: { message?: string }; message?: string };
      if (typeof maybeError.error?.message === 'string') {
        const nested = maybeError.error.message.trim();
        if (nested) return nested;
      }
      if (typeof maybeError.message === 'string') {
        const nested = maybeError.message.trim();
        if (nested) return nested;
      }
    }
    return fallback;
  }

  private parseOptionalNumber(value: OptionalNumberInput): number | null {
    if (value === null || value === '') return null;
    const parsed = typeof value === 'number' ? value : Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /** Exposed for template bindings on dropdown selection. */
  parseDropdownNumber(value: OptionalNumberInput): number | null {
    return this.parseOptionalNumber(value);
  }

  setCreateGrowerAccountId(value: OptionalNumberInput): void {
    this.create.growerAccountId.set(this.parseOptionalNumber(value));
  }

  setCreateTransloadingStationId(value: OptionalNumberInput): void {
    this.create.transloadingStationId.set(this.parseOptionalNumber(value));
  }

  setCreateShuntingYardId(value: OptionalNumberInput): void {
    this.create.shuntingYardId.set(this.parseOptionalNumber(value));
  }

  setCreatePlantId(value: OptionalNumberInput): void {
    this.create.plantId.set(this.parseOptionalNumber(value));
  }

  async refreshLists(): Promise<void> {
    this.isLoading.set(true);
    this.errorText.set(null);
    try {
      const roles = this.roleSections.map(section => section.role);
      const responses = await Promise.all(
        roles.map(role => this.users.getUsersByRoleMappings(role, { includeInactive: true }))
      );
      const nextUsers: RoleUsersRecord = { ...EMPTY_ROLE_USERS };
      for (let index = 0; index < roles.length; index += 1) {
        nextUsers[roles[index]] = responses[index].users || [];
      }
      this.roleUsers.set(nextUsers);
      this.roleFilters.set(this.getValidRoleFilters(nextUsers, this.roleFilters()));
      this.roleUserFilters.set(this.getValidRoleUserFilters(nextUsers, this.roleUserFilters()));
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to load users'));
    } finally {
      this.isLoading.set(false);
    }
  }

  async loadReferenceData(): Promise<void> {
    this.isReferenceLoading.set(true);
    try {
      const [accounts, stations, plants] = await Promise.all([
        this.referenceData.getGrowerAccounts(),
        this.referenceData.getTransloadingStations(),
        this.referenceData.getPlants()
      ]);
      this.growerAccounts.set(accounts.growerAccounts || []);
      this.transloadingStations.set(stations.transloadingStations || []);
      this.plants.set(plants.plants || []);
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to load reference data'));
    } finally {
      this.isReferenceLoading.set(false);
    }
  }

  onSelectCreateRole(role: ExternalRoleName): void {
    this.create.role.set(role);
    this.resetCreateMappings();
  }

  onSelectCreateRoleValue(value: string): void {
    const role = this.toExternalRoleName(value);
    if (role) {
      this.onSelectCreateRole(role);
    }
  }

  private resetCreateMappings(): void {
    this.create.growerAccountId.set(null);
    this.create.transloadingStationId.set(null);
    this.create.shuntingPlantId.set(null);
    this.create.shuntingYardId.set(null);
    this.create.plantId.set(null);
    this.create.schedulerPlantIds.set([]);
    this.shuntingYards.set([]);
  }

  async onShuntingPlantChange(plantId: number | null): Promise<void> {
    this.create.shuntingPlantId.set(plantId);
    this.create.shuntingYardId.set(null);
    this.shuntingYards.set([]);
    if (plantId === null) return;
    try {
      const yards = await this.referenceData.getShuntingYardsByPlantId(plantId);
      this.shuntingYards.set(yards.shuntingYards || []);
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to load shunting yards'));
    }
  }

  onShuntingPlantSelection(value: OptionalNumberInput): void {
    void this.onShuntingPlantChange(this.parseOptionalNumber(value));
  }

  isSchedulerPlantSelected(plantId: number): boolean {
    const selected = new Set(this.create.schedulerPlantIds());
    return selected.has(plantId);
  }

  toggleSchedulerPlant(plantId: number): void {
    const current = this.create.schedulerPlantIds();
    const selected = new Set(current);
    if (selected.has(plantId)) {
      selected.delete(plantId);
    } else {
      selected.add(plantId);
    }
    this.create.schedulerPlantIds.set(Array.from(selected));
  }

  private getCreateRequest(): { body: CreateExternalUserRequest | null; error: string | null } {
    const role = this.create.role();
    const roleName = this.roleToName(role);
    const body: CreateExternalUserRequest = {
      username: this.create.username().trim(),
      employeeName: this.create.employeeName().trim(),
      roleName
    };

    if (!body.username || !body.employeeName) {
      return { body: null, error: 'Username and Employee Name are required' };
    }

    switch (role) {
      case 'Grower': {
        const growerAccountId = this.create.growerAccountId();
        if (growerAccountId === null) {
          return { body: null, error: 'Grower Account is required for Grower role' };
        }
        body.growerAccountID = growerAccountId;
        break;
      }
      case 'Transloader': {
        const stationId = this.create.transloadingStationId();
        if (stationId === null) {
          return { body: null, error: 'Transloading Station is required for Transloader role' };
        }
        body.transloadingStationID = stationId;
        break;
      }
      case 'Shunting': {
        const shuntingPlantId = this.create.shuntingPlantId();
        const shuntingYardId = this.create.shuntingYardId();
        if (shuntingPlantId === null || shuntingYardId === null) {
          return { body: null, error: 'Plant and Shunting Yard are required for Shunting role' };
        }
        body.shuntingPlantID = shuntingPlantId;
        body.shuntingYardID = shuntingYardId;
        break;
      }
      case 'Gatehouse':
      case 'Receiver': {
        const plantId = this.create.plantId();
        if (plantId === null) {
          return { body: null, error: 'Plant is required for this role' };
        }
        body.plantID = plantId;
        break;
      }
      case 'Scheduler': {
        const plantIds = this.create.schedulerPlantIds();
        if (plantIds.length === 0) {
          return { body: null, error: 'Select at least one Plant for Scheduler role' };
        }
        body.plantIDs = plantIds;
        break;
      }
      default:
        break;
    }

    return { body, error: null };
  }

  trackGrowerAccount(_: number, item: GrowerAccountSummary): number {
    return item.growerAccountID;
  }

  trackTransloadingStation(_: number, item: TransloadingStationSummary): number {
    return item.transloadingStationID;
  }

  trackPlant(_: number, item: PlantSummary): number {
    return item.plantID;
  }

  trackShuntingYard(_: number, item: ShuntingYardSummary): number {
    return item.shuntingYardID;
  }

  trackRoleSection(_: number, item: RoleSection): ExternalRoleName {
    return item.role;
  }

  trackRoleUser(index: number, item: RoleUserMappingSummary): string {
    const mappingValue = item.mappingID ?? item.mappingName ?? 'none';
    return `${item.userID}-${mappingValue}-${index}`;
  }

  trackSchedulerGrouped(_: number, item: SchedulerGroupedRow): string {
    return String(item.userID);
  }

  trackMappingOption(_: number, item: string): string {
    return item;
  }

  getRoleDropdownOptions(): DropdownOption[] {
    return ROLE_OPTIONS;
  }

  shouldShowRoleFilter(section: RoleSection): boolean {
    return Boolean(section.mappingLabel);
  }

  shouldShowUsernameFilter(section: RoleSection): boolean {
    return ROLE_WITH_USERNAME_FILTER.has(section.role);
  }

  getRoleFilterLabel(section: RoleSection): string {
    return section.mappingLabel ?? 'User';
  }

  getRoleFilterOptions(role: ExternalRoleName, label: string): DropdownOption[] {
    const options = ROLE_WITH_MAPPING.has(role)
      ? this.getMappingOptions(role).map(option => ({ value: option, label: option }))
      : this.getUserFilterOptions(this.getEligibleRoleUsers(role, this.getRoleUsers(role)));
    return [{ value: '', label: `All ${label}` }, ...options];
  }

  getRoleUserFilterOptions(role: ExternalRoleName): DropdownOption[] {
    const options = this.getUserFilterOptions(this.getEligibleRoleUsers(role, this.getRoleUsers(role)));
    return [{ value: '', label: 'All Usernames' }, ...options];
  }

  getRoleFilterValue(role: ExternalRoleName): string {
    return this.getRoleFilter(role) ?? '';
  }

  getRoleUserFilterValue(role: ExternalRoleName): string {
    return this.getRoleUserFilter(role) ?? '';
  }

  onRoleFilterChange(role: ExternalRoleName, value: string): void {
    const nextValue = value === '' ? null : value;
    this.setRoleFilter(role, nextValue);
  }

  onRoleUserFilterChange(role: ExternalRoleName, value: string): void {
    const nextValue = value === '' ? null : value;
    this.setRoleUserFilter(role, nextValue);
  }

  getGrowerAccountOptions(): DropdownOption[] {
    return this.growerAccounts().map(account => ({
      value: String(account.growerAccountID),
      label: `${account.growerAccountName} (${account.vendorNumber})`
    }));
  }

  getTransloadingStationOptions(): DropdownOption[] {
    return this.transloadingStations().map(station => ({
      value: String(station.transloadingStationID),
      label: `${station.transloadingStationName} (${station.transloadingSite})`
    }));
  }

  getPlantOptions(): DropdownOption[] {
    return this.plants()
      .filter(plant => !this.isExcludedPlant(plant))
      .map(plant => ({
        value: String(plant.plantID),
        label: plant.plantName
      }));
  }

  /** Plant dropdown for Shunting create flow — Whittlesey only. */
  getShuntingCreatePlantOptions(): DropdownOption[] {
    return this.plants()
      .filter(plant => this.isWhittleseyShuntingPlant(plant))
      .map(plant => ({
        value: String(plant.plantID),
        label: plant.plantName
      }));
  }

  private isWhittleseyShuntingPlant(plant: PlantSummary): boolean {
    return plant.plantName.trim().toLowerCase() === SHUNTING_CREATE_PLANT_NAME;
  }

  getSchedulerPlants(): PlantSummary[] {
    return this.plants().filter(plant => !this.isExcludedPlant(plant));
  }

  getShuntingYardOptions(): DropdownOption[] {
    return this.shuntingYards().map(yard => ({
      value: String(yard.shuntingYardID),
      label: yard.shuntingYardName
    }));
  }

  getDropdownValue(value: number | null): string | null {
    return value === null ? null : String(value);
  }

  isRoleOpen(role: ExternalRoleName): boolean {
    return this.roleOpen()[role];
  }

  toggleRoleOpen(role: ExternalRoleName): void {
    this.roleOpen.update(state => ({ ...state, [role]: !state[role] }));
  }

  getRoleUsers(role: ExternalRoleName): RoleUserMappingSummary[] {
    return this.roleUsers()[role];
  }

  private hasRoleMapping(user: RoleUserMappingSummary): boolean {
    if (user.mappingID === null || user.mappingID === undefined) return false;
    const name = (user.mappingName ?? '').trim();
    return name.length > 0;
  }

  private getEligibleRoleUsers(
    role: ExternalRoleName,
    users: RoleUserMappingSummary[]
  ): RoleUserMappingSummary[] {
    if (!ROLE_WITH_MAPPING.has(role)) return users;
    return users.filter(user => this.hasRoleMapping(user));
  }

  getRoleFilter(role: ExternalRoleName): string | null {
    return this.roleFilters()[role];
  }

  setRoleFilter(role: ExternalRoleName, value: string | null): void {
    this.roleFilters.update(state => ({ ...state, [role]: value }));
  }

  getRoleUserFilter(role: ExternalRoleName): string | null {
    return this.roleUserFilters()[role];
  }

  setRoleUserFilter(role: ExternalRoleName, value: string | null): void {
    this.roleUserFilters.update(state => ({ ...state, [role]: value }));
  }

  private filterRoleUsers(role: ExternalRoleName, users: RoleUserMappingSummary[]): RoleUserMappingSummary[] {
    const eligibleUsers = this.getEligibleRoleUsers(role, users);
    let filteredUsers = eligibleUsers;
    const mappingFilter = this.getRoleFilter(role);
    if (mappingFilter) {
      if (ROLE_WITH_MAPPING.has(role)) {
        filteredUsers = filteredUsers.filter(user => this.getMappingParts(user).includes(mappingFilter));
      } else {
        filteredUsers = filteredUsers.filter(user => String(user.userID) === mappingFilter);
      }
    }
    const userFilter = this.getRoleUserFilter(role);
    if (userFilter) {
      filteredUsers = filteredUsers.filter(user => String(user.userID) === userFilter);
    }
    return filteredUsers;
  }

  getFilteredUsers(role: ExternalRoleName): RoleUserMappingSummary[] {
    return this.filterRoleUsers(role, this.getRoleUsers(role));
  }

  private groupSchedulerUsers(rows: RoleUserMappingSummary[]): SchedulerGroupedRow[] {
    const byUser = new Map<number, RoleUserMappingSummary[]>();
    for (const row of rows) {
      const list = byUser.get(row.userID) ?? [];
      list.push(row);
      byUser.set(row.userID, list);
    }
    const result: SchedulerGroupedRow[] = [];
    for (const [, group] of byUser) {
      const sorted = [...group].sort((a, b) =>
        (a.mappingName ?? '').localeCompare(b.mappingName ?? '', undefined, { sensitivity: 'base' }));
      const labels = [...new Set(sorted.map(r => (r.mappingName ?? '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }));
      const plantIds = [
        ...new Set(
          sorted
            .map(r => r.mappingID)
            .filter((id): id is number => id !== null && id !== undefined && id > 0)
        )
      ];
      const first = sorted[0];
      const anyActive = sorted.some(r => this.isUserActive(r));
      result.push({
        ...first,
        mappingName: labels.join(', '),
        mappingID: plantIds[0] ?? first.mappingID ?? null,
        isActive: anyActive,
        schedulerPlantIds: plantIds
      });
    }
    return result.sort((a, b) =>
      (a.employeeName || a.username).localeCompare(b.employeeName || b.username, undefined, { sensitivity: 'base' }));
  }

  getSchedulerGroupedUsers(): SchedulerGroupedRow[] {
    const filtered = this.filterRoleUsers('Scheduler', this.getRoleUsers('Scheduler'));
    return this.groupSchedulerUsers(filtered);
  }

  getSectionUserCount(section: RoleSection): number {
    if (section.role === 'Scheduler') {
      return this.getSchedulerGroupedUsers().length;
    }
    return this.getFilteredUsers(section.role).length;
  }

  getMappingOptions(role: ExternalRoleName): string[] {
    const users = this.getEligibleRoleUsers(role, this.getRoleUsers(role));
    return Array.from(this.getMappingOptionSet(users)).sort((a, b) => a.localeCompare(b));
  }

  formatMappingName(user: RoleUserMappingSummary): string {
    const name = (user.mappingName ?? '').trim();
    return name || 'Unassigned';
  }

  isUserActive(user: RoleUserMappingSummary): boolean {
    return user.isActive ?? true;
  }

  getActivateTitle(user: RoleUserMappingSummary, role: ExternalRoleName): string {
    if (this.isUserActive(user)) return '';
    if (ROLE_WITH_MAPPING.has(role)) {
      if (user.mappingID === null || user.mappingID === undefined) {
        const label = this.getRoleMappingLabel(role) ?? 'mapping';
        return `Create ${label} below to activate`;
      }
    }
    return 'Activate';
  }

  private getRoleMappingLabel(role: ExternalRoleName): string | null {
    const section = this.roleSections.find(item => item.role === role);
    return section?.mappingLabel ?? null;
  }

  private getMappingParts(user: RoleUserMappingSummary): string[] {
    const name = (user.mappingName ?? '').trim();
    if (!name) return ['Unassigned'];
    return name
      .split(',')
      .map(part => part.trim())
      .filter(part => part.length > 0);
  }

  private getMappingOptionSet(users: RoleUserMappingSummary[]): Set<string> {
    const options = new Set<string>();
    for (const user of users) {
      for (const part of this.getMappingParts(user)) {
        options.add(part);
      }
    }
    return options;
  }

  private isExcludedPlant(plant: PlantSummary): boolean {
    const plantName = plant.plantName.trim().toLowerCase();
    return EXCLUDED_PLANTS.has(plantName);
  }

  private getUserFilterOptions(users: RoleUserMappingSummary[]): DropdownOption[] {
    const options = users.map(user => ({
      value: String(user.userID),
      label: this.formatUserFilterLabel(user)
    }));
    return [...options].sort((a, b) => a.label.localeCompare(b.label));
  }

  private formatUserFilterLabel(user: RoleUserMappingSummary): string {
    const name = user.employeeName?.trim() ?? '';
    const username = user.username?.trim() ?? '';
    if (name && username) return `${name} (${username})`;
    return name || username || `User ${user.userID}`;
  }

  private getRoleFilterOptionSet(role: ExternalRoleName, users: RoleUserMappingSummary[]): Set<string> {
    const eligibleUsers = this.getEligibleRoleUsers(role, users);
    if (ROLE_WITH_MAPPING.has(role)) {
      return this.getMappingOptionSet(eligibleUsers);
    }
    return new Set(eligibleUsers.map(user => String(user.userID)));
  }

  private getRoleUserFilterOptionSet(role: ExternalRoleName, users: RoleUserMappingSummary[]): Set<string> {
    const eligibleUsers = this.getEligibleRoleUsers(role, users);
    return new Set(eligibleUsers.map(user => String(user.userID)));
  }

  private getValidRoleFilters(
    nextUsers: RoleUsersRecord,
    currentFilters: RoleFilterRecord
  ): RoleFilterRecord {
    const nextFilters: RoleFilterRecord = { ...currentFilters };
    for (const section of this.roleSections) {
      const role = section.role;
      const filterValue = currentFilters[role];
      if (!filterValue) continue;
      const options = this.getRoleFilterOptionSet(role, nextUsers[role]);
      if (!options.has(filterValue)) {
        nextFilters[role] = null;
      }
    }
    return nextFilters;
  }

  private getValidRoleUserFilters(
    nextUsers: RoleUsersRecord,
    currentFilters: RoleUserFilterRecord
  ): RoleUserFilterRecord {
    const nextFilters: RoleUserFilterRecord = { ...currentFilters };
    for (const section of this.roleSections) {
      const role = section.role;
      const filterValue = currentFilters[role];
      if (!filterValue) continue;
      const options = this.getRoleUserFilterOptionSet(role, nextUsers[role]);
      if (!options.has(filterValue)) {
        nextFilters[role] = null;
      }
    }
    return nextFilters;
  }

  async onCreate(): Promise<void> {
    this.errorText.set(null);
    const { body, error } = this.getCreateRequest();
    if (!body) {
      this.errorText.set(error || 'Failed to create user');
      return;
    }
    this.isCreateSaving.set(true);
    try {
      await this.users.createExternalUser(body);
      await this.refreshLists();
      this.create.username.set('');
      this.create.employeeName.set('');
      this.resetCreateMappings();
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to create user'));
    } finally {
      this.isCreateSaving.set(false);
    }
  }

  openGrowerAccountModal(context: 'create' | 'edit' = 'create'): void {
    this.growerAccountModalContext.set(context);
    this.growerAccountName.set('');
    this.growerAccountVendorNumber.set('');
    this.showGrowerAccountModal.set(true);
  }

  closeGrowerAccountModal(): void {
    this.showGrowerAccountModal.set(false);
  }

  openEditGrowerAccountModal(): void {
    const accId = this.editGrowerAccountId();
    if (accId === null) return;
    const acc = this.growerAccounts().find(a => a.growerAccountID === accId);
    if (!acc) return;
    this.editGrowerAccountModalName.set(acc.growerAccountName.trim());
    this.editGrowerAccountModalVendorNumber.set(acc.vendorNumber.trim());
    this.showEditGrowerAccountModal.set(true);
  }

  closeEditGrowerAccountModal(): void {
    this.showEditGrowerAccountModal.set(false);
    this.editGrowerAccountModalName.set('');
    this.editGrowerAccountModalVendorNumber.set('');
  }

  isEditGrowerAccountSaveDisabled(): boolean {
    if (this.isEditGrowerAccountSaving()) return true;
    return !this.editGrowerAccountModalName().trim() || !this.editGrowerAccountModalVendorNumber().trim();
  }

  async onSaveEditGrowerAccount(): Promise<void> {
    if (this.isEditGrowerAccountSaveDisabled()) return;
    const accId = this.editGrowerAccountId();
    if (accId === null) return;
    this.isEditGrowerAccountSaving.set(true);
    try {
      await this.referenceData.updateGrowerAccount(accId, {
        growerAccountName: this.editGrowerAccountModalName().trim(),
        vendorNumber: this.editGrowerAccountModalVendorNumber().trim()
      });
      const accounts = await this.referenceData.getGrowerAccounts();
      this.growerAccounts.set(accounts.growerAccounts || []);
      this.closeEditGrowerAccountModal();
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to update grower account'));
    } finally {
      this.isEditGrowerAccountSaving.set(false);
    }
  }

  isGrowerAccountCreateDisabled(): boolean {
    if (this.isGrowerAccountSaving()) return true;
    const name = this.growerAccountName().trim();
    const vendorNumber = this.growerAccountVendorNumber().trim();
    return !name || !vendorNumber;
  }

  async onCreateGrowerAccount(): Promise<void> {
    if (this.isGrowerAccountCreateDisabled()) return;
    this.isGrowerAccountSaving.set(true);
    try {
      const body: CreateGrowerAccountRequest = {
        growerAccountName: this.growerAccountName().trim(),
        vendorNumber: this.growerAccountVendorNumber().trim()
      };
      const response = await this.referenceData.createGrowerAccount(body);
      const accounts = await this.referenceData.getGrowerAccounts();
      this.growerAccounts.set(accounts.growerAccounts || []);
      const newId = response.growerAccount.growerAccountID;
      if (this.growerAccountModalContext() === 'edit') {
        this.editGrowerAccountId.set(newId);
      } else {
        this.create.growerAccountId.set(newId);
      }
      this.closeGrowerAccountModal();
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to create grower account'));
    } finally {
      this.isGrowerAccountSaving.set(false);
    }
  }

  async onDeactivate(u: RoleUserMappingSummary, role: ExternalRoleName): Promise<void> {
    if (this.isDeactivateDisabled(u)) {
      this.errorText.set('You cannot deactivate your own account');
      return;
    }
    this.openConfirmModal('deactivate', u, role);
  }

  async onActivate(u: RoleUserMappingSummary, role: ExternalRoleName): Promise<void> {
    if (this.isUserActive(u)) return;

    const requiresMapping = ROLE_WITH_MAPPING.has(role);
    const mappingId = u.mappingID ?? null;
    if (requiresMapping && mappingId === null) {
      const roleLabel = this.getRoleMappingLabel(role) ?? 'mapping';
      this.errorText.set(`${roleLabel} is required to activate this role`);
      return;
    }

    this.openConfirmModal('activate', u, role);
  }

  getConfirmActionLabel(): string {
    return this.confirmAction() === 'activate' ? 'Activate' : 'Deactivate';
  }

  openConfirmModal(action: ConfirmAction, user: RoleUserMappingSummary, role: ExternalRoleName): void {
    const title = action === 'activate' ? `Activate ${role} role` : `Deactivate ${role} role`;
    const message = action === 'activate'
      ? this.buildActivateMessage(user, role)
      : this.buildDeactivateMessage(user, role);
    this.confirmTitle.set(title);
    this.confirmMessage.set(message);
    this.confirmAction.set(action);
    this.confirmUser.set(user);
    this.confirmRole.set(role);
    this.showConfirmModal.set(true);
  }

  closeConfirmModal(): void {
    this.showConfirmModal.set(false);
    this.confirmAction.set(null);
    this.confirmUser.set(null);
    this.confirmRole.set(null);
  }

  async onConfirmAction(): Promise<void> {
    const action = this.confirmAction();
    const user = this.confirmUser();
    const role = this.confirmRole();
    this.closeConfirmModal();
    if (!action || !user || !role) return;
    if (action === 'activate') {
      await this.performActivate(user, role);
    } else {
      await this.performDeactivate(user, role);
    }
  }

  private buildDeactivateMessage(user: RoleUserMappingSummary, role: ExternalRoleName): string {
    const userName = user.employeeName || user.username;
    const userEmail = user.username;
    const mappingName = this.formatMappingName(user);
    const schedulerExtra =
      role === 'Scheduler'
        ? `Plants: ${mappingName}\n`
        : '';
    const schedulerFooter =
      role === 'Scheduler'
        ? 'This removes Scheduler access for all listed plants (full Scheduler role removal).\n\n'
        : '';
    return `Are you sure you want to deactivate this user's ${role} role?\n\n` +
      `User: ${userName}\n` +
      `Email: ${userEmail}\n` +
      `Role: ${role}\n` +
      schedulerExtra +
      `\n` +
      schedulerFooter +
      `This action will prevent the user from accessing the system with this role. You can reactivate them later if needed.`;
  }

  private buildActivateMessage(user: RoleUserMappingSummary, role: ExternalRoleName): string {
    const userName = user.employeeName || user.username;
    const userEmail = user.username;
    const mappingLabel = this.getRoleMappingLabel(role);
    const mappingName = mappingLabel ? this.formatMappingName(user) : '';
    return `Are you sure you want to activate this user's ${role} role?\n\n` +
      `User: ${userName}\n` +
      `Email: ${userEmail}\n` +
      `Role: ${role}\n` +
      (mappingLabel ? `${mappingLabel}: ${mappingName}\n` : '') +
      `\n` +
      `This action will restore the user's access to this role.`;
  }

  private async performDeactivate(u: RoleUserMappingSummary, role: ExternalRoleName): Promise<void> {
    try {
      const roleId = ROLE_ID_MAP[role];
      await this.users.deactivateUser(u.userID, roleId);
      await this.refreshLists();
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to deactivate user'));
    }
  }

  private async performActivate(u: RoleUserMappingSummary, role: ExternalRoleName): Promise<void> {
    try {
      const roleId = ROLE_ID_MAP[role];
      const requiresMapping = ROLE_WITH_MAPPING.has(role);
      const mappingId = u.mappingID ?? null;
      await this.users.activateUser(
        u.userID,
        roleId,
        requiresMapping ? mappingId ?? undefined : undefined
      );
      await this.refreshLists();
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to activate user'));
    }
  }

  private resetEditForm(): void {
    this.editRole.set(null);
    this.editUserId.set(null);
    this.editEmployeeName.set('');
    this.editUsername.set('');
    this.editGrowerAccountId.set(null);
    this.editTransloadingStationId.set(null);
    this.editShuntingPlantId.set(null);
    this.editShuntingYardId.set(null);
    this.editPlantId.set(null);
    this.editSchedulerPlantIds.set([]);
    this.editShuntingYards.set([]);
  }

  closeEditModal(): void {
    this.showEditModal.set(false);
    this.resetEditForm();
  }

  /** DB maps Shunting MappingID to ShuntingYardID; resolve parent plant (Whittlesey-only rule). */
  private async findPlantIdForShuntingYard(yardId: number): Promise<number | null> {
    const candidates = this.plants().filter(plant => this.isWhittleseyShuntingPlant(plant));
    for (const plant of candidates) {
      try {
        const yards = await this.referenceData.getShuntingYardsByPlantId(plant.plantID);
        if ((yards.shuntingYards ?? []).some(y => y.shuntingYardID === yardId)) {
          return plant.plantID;
        }
      } catch {
        continue;
      }
    }
    return null;
  }

  async openEditModal(user: RoleUserMappingSummary, role: ExternalRoleName): Promise<void> {
    if (this.isEditDisabled(user)) {
      this.errorText.set('You cannot edit your own account');
      return;
    }
    this.clearError();
    this.resetEditForm();
    this.editRole.set(role);
    this.editUserId.set(user.userID);
    this.editEmployeeName.set((user.employeeName ?? '').trim());
    this.editUsername.set((user.username ?? '').trim());

    if (role === 'Grower') {
      this.editGrowerAccountId.set(user.mappingID ?? null);
    } else if (role === 'Transloader') {
      this.editTransloadingStationId.set(user.mappingID ?? null);
    } else if (role === 'Shunting') {
      const yardId = user.mappingID ?? null;
      this.editShuntingYardId.set(yardId);
      this.editShuntingPlantId.set(null);
      this.editShuntingYards.set([]);
      if (yardId !== null && yardId > 0) {
        try {
          const plantId = await this.findPlantIdForShuntingYard(yardId);
          this.editShuntingPlantId.set(plantId);
          if (plantId !== null) {
            const yards = await this.referenceData.getShuntingYardsByPlantId(plantId);
            this.editShuntingYards.set(yards.shuntingYards || []);
          }
        } catch (error: unknown) {
          this.errorText.set(this.getErrorMessage(error, 'Failed to load shunting yards'));
          return;
        }
      }
    } else if (role === 'Gatehouse' || role === 'Receiver') {
      this.editPlantId.set(user.mappingID ?? null);
    } else if (role === 'Scheduler') {
      const sg = user as SchedulerGroupedRow;
      this.editSchedulerPlantIds.set([...(sg.schedulerPlantIds ?? [])]);
    }

    this.showEditModal.set(true);
  }

  setEditGrowerAccountId(value: OptionalNumberInput): void {
    this.editGrowerAccountId.set(this.parseOptionalNumber(value));
  }

  async onEditShuntingPlantSelection(value: OptionalNumberInput): Promise<void> {
    const plantId = this.parseOptionalNumber(value);
    this.editShuntingPlantId.set(plantId);
    this.editShuntingYardId.set(null);
    this.editShuntingYards.set([]);
    if (plantId === null) return;
    try {
      const yards = await this.referenceData.getShuntingYardsByPlantId(plantId);
      this.editShuntingYards.set(yards.shuntingYards || []);
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to load shunting yards'));
    }
  }

  getEditShuntingYardOptions(): DropdownOption[] {
    return this.editShuntingYards().map(yard => ({
      value: String(yard.shuntingYardID),
      label: yard.shuntingYardName
    }));
  }

  isEditSchedulerPlantSelected(plantId: number): boolean {
    return new Set(this.editSchedulerPlantIds()).has(plantId);
  }

  toggleEditSchedulerPlant(plantId: number): void {
    const selected = new Set(this.editSchedulerPlantIds());
    if (selected.has(plantId)) {
      selected.delete(plantId);
    } else {
      selected.add(plantId);
    }
    this.editSchedulerPlantIds.set(Array.from(selected));
  }

  private buildEditMappingBody(role: ExternalRoleName): UpdateExternalUserRoleMappingRequest | null {
    const roleName = this.roleToName(role);
    switch (role) {
      case 'Grower': {
        const growerAccountID = this.editGrowerAccountId();
        if (growerAccountID === null) return null;
        return { roleName, growerAccountID };
      }
      case 'Transloader': {
        const transloadingStationID = this.editTransloadingStationId();
        if (transloadingStationID === null) return null;
        return { roleName, transloadingStationID };
      }
      case 'Shunting': {
        const shuntingPlantID = this.editShuntingPlantId();
        const shuntingYardID = this.editShuntingYardId();
        if (shuntingPlantID === null || shuntingYardID === null) return null;
        return { roleName, shuntingPlantID, shuntingYardID };
      }
      case 'Gatehouse':
      case 'Receiver': {
        const plantID = this.editPlantId();
        if (plantID === null) return null;
        return { roleName, plantID };
      }
      case 'Scheduler': {
        const plantIDs = this.editSchedulerPlantIds();
        if (plantIDs.length === 0) return null;
        return { roleName, plantIDs };
      }
      default:
        return null;
    }
  }

  isEditSaveDisabled(): boolean {
    if (this.isEditSaving()) return true;
    const username = this.editUsername().trim();
    const employeeName = this.editEmployeeName().trim();
    if (!username || !employeeName) return true;
    const role = this.editRole();
    if (!role) return true;
    if (!ROLE_WITH_MAPPING.has(role)) return false;
    const mapping = this.buildEditMappingBody(role);
    return mapping === null;
  }

  async onSaveEdit(): Promise<void> {
    if (this.isEditSaveDisabled()) return;
    const role = this.editRole();
    const userId = this.editUserId();
    if (!role || userId === null) return;

    this.isEditSaving.set(true);
    this.errorText.set(null);
    try {
      await this.users.updateExternalUser(userId, {
        username: this.editUsername().trim(),
        employeeName: this.editEmployeeName().trim()
      });

      if (ROLE_WITH_MAPPING.has(role)) {
        const mappingBody = this.buildEditMappingBody(role);
        if (mappingBody !== null) {
          await this.users.updateExternalUserRoleMapping(userId, mappingBody);
        }
      }

      this.closeEditModal();
      await this.refreshLists();
    } catch (error: unknown) {
      this.errorText.set(this.getErrorMessage(error, 'Failed to save changes'));
    } finally {
      this.isEditSaving.set(false);
    }
  }

  onNavChange(id: string): void {
    switch ((id || '').toLowerCase()) {
      case 'profile':
        this.router.navigateByUrl('/profile');
        break;
      case 'home': {
        // If user is on admin page, they must have admin persona (persona guard ensures this)
        // Admin's home is the admin page
        this.router.navigateByUrl('/admin');
        break;
      }
      case 'contacts':
        this.router.navigateByUrl('/contacts');
        break;
      default:
        this.router.navigateByUrl('/admin');
    }
  }

}


